import { useId } from "react";
import { cn } from "@/lib/utils";

const DOTS: [number, number, number][] = [
	[8.5, 23.5, 1.5],
	[16, 23.5, 1.95],
	[8.5, 16, 1.95],
	[23.5, 23.5, 2.4],
	[16, 16, 2.4],
	[8.5, 8.5, 2.4],
	[23.5, 16, 2.85],
	[16, 8.5, 2.85],
	[23.5, 8.5, 3.3],
];

function MarkShape({ maskId }: { maskId: string }) {
	return (
		<>
			<mask id={maskId}>
				<rect width="32" height="32" fill="#fff" />
				{DOTS.map(([cx, cy, r]) => (
					<circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={r} fill="#000" />
				))}
			</mask>
			<rect
				x="1"
				y="1"
				width="30"
				height="30"
				rx="7"
				fill="currentColor"
				mask={`url(#${maskId})`}
			/>
		</>
	);
}

/** The dot-grid tile. Inherits `currentColor`, so it inverts with the theme. */
export function LogoMark({ className, ...props }: React.ComponentProps<"svg">) {
	const maskId = useId();
	return (
		<svg
			viewBox="0 0 32 32"
			fill="none"
			aria-label="Gridcast"
			role="img"
			className={cn("size-6", className)}
			{...props}
		>
			<MarkShape maskId={maskId} />
		</svg>
	);
}

/** Mark + mono-line wordmark. Height-driven: set `h-*` and width follows. */
export function Logo({ className, ...props }: React.ComponentProps<"svg">) {
	const maskId = useId();
	return (
		<svg
			viewBox="0 0 140 32"
			fill="none"
			aria-label="Gridcast"
			role="img"
			className={cn("h-6 w-auto", className)}
			{...props}
		>
			<MarkShape maskId={maskId} />
			<g
				transform="translate(43 0)"
				stroke="currentColor"
				strokeWidth="2.4"
				strokeLinecap="round"
				strokeLinejoin="round"
			>
				<circle cx="6" cy="17" r="6" />
				<path d="M12 11v13a5 5 0 0 1-5 5H3.5" />
				<path d="M16.2 23V11m0 6a6 6 0 0 1 6-6h1.5" />
				<path d="M26.8 23V11" />
				<circle cx="37" cy="17" r="6" />
				<path d="M43 5v18" />
				<path d="M57.06 12.4a6 6 0 1 0 0 9.2" />
				<circle cx="66.5" cy="17" r="6" />
				<path d="M72.5 11v12" />
				<path d="M84.4 12.8c-.9-1.1-2.3-1.8-4.2-1.8-2.5 0-4.3 1.3-4.3 3.2 0 2 1.8 2.6 4.3 3.1 2.6.5 4.5 1.2 4.5 3.3 0 2-1.9 3.4-4.6 3.4-2.1 0-3.7-.8-4.7-2.1" />
				<path d="M90.3 6v13.5a3.5 3.5 0 0 0 3.5 3.5h1.5M87.3 11h7.5" />
			</g>
			<circle cx="69.8" cy="6.2" r="1.7" fill="currentColor" />
		</svg>
	);
}
